import { constrain } from 'utils/math';
import { Events } from 'core/events/Events';
import { DirectionalKeyType } from 'app/data/Enums';
import { DebugMouseWheelEvent } from 'app/events/DebugMouseWheelEvent';
import { DebugKeypressEvent } from 'app/events/DebugKeypressEvent';
import { ScrollbarInputDeps } from './scrollbar.types';

/**
 * User input for the scrollbar: the mouse-wheel handler (with the
 * human-vs-inertia heuristic and Safari/Firefox/Mac tuning), the directional
 * keyboard handler and the touch-drag state. Event registration
 * (addEventListener / Events.on) stays in ScrollbarController (handlers
 * delegate here); active / paused / dragging flags and the engine live
 * there too and arrive via the injected deps.
 */
export class ScrollbarInput {
	private _deps: ScrollbarInputDeps;


	//Scrollwheel limiters:
	private _scrollWheelRecord: number = 0;

	private SCROLLWHEEL_LIMITER_FRAME_REDUCE: number = 20; //Every frame reduce record by this amount, will stop mouse moving too far

	constructor(deps: ScrollbarInputDeps) {
		this._deps = deps;
	}

	/** Safari scrollwheel tuning (called from ScrollbarController.init at the pre-split spot) */
	public applySafariOverrides(): void {
		this.SCROLLWHEEL_LIMITER_FRAME_REDUCE = 40; //Every frame reduce record by this amount, will stop mouse moving too far
	}

	/** On Touch start

	//** On mouse wheel scrollet */
	private lastScrollWheelTimestamp = 0;
	private lastScrollWheelDelta = 0;
	private minScrollWheelInterval = 100;
	private speedDecreaseFalseFrames = 0;

	public onMouseWheel(e): void {
		if (this._deps.isPaused()) {
			return;
		}

		//Other controllers will need to use this wheel scroll event

		let realDelta = e.wheelDelta || -e.detail;
		let delta: number = Math.max(-1, Math.min(1, realDelta));

		const now = Date.now();

		const rapidSuccession = now - this.lastScrollWheelTimestamp < this.minScrollWheelInterval;
		const otherDirection = this.lastScrollWheelDelta > 0 !== e.deltaY > 0;
		let speedDecrease = true;

		if (Math.abs(e.deltaY) <= Math.abs(this.lastScrollWheelDelta)) {
			this.speedDecreaseFalseFrames = 0;
		} else {
			this.speedDecreaseFalseFrames++;
			if (this.speedDecreaseFalseFrames >= 2) {
				speedDecrease = false;
			}
		}

		let isHuman = otherDirection || !rapidSuccession || !speedDecrease;

		if (isHuman) {
			this.speedDecreaseFalseFrames = 0;
		}

		this.lastScrollWheelTimestamp = now;
		this.lastScrollWheelDelta = e.deltaY;

		//If active or dragging then there is page target that
		if (!this._deps.isActive() || this._deps.isDragging() || this._deps.isHideScroller()) {
			//If no target then send event as it can used by the scenes that have side parallax scrolling
			if (!this._deps.isActive()) {
				Events.dispatch(new DebugMouseWheelEvent({ delta: delta, realDelta: realDelta, isHuman: isHuman }));
			}
			return;
		}

		//Firefox fires -3 - 3. So handle that
		let scrollReal = !this._deps.isFirefox() ? realDelta : realDelta * 40;

		//Switch between Safari + PC type browsers:
		let scroll = this._deps.isSafari() ? realDelta : scrollReal;

		if (this._deps.isMac()) {
			scroll = scroll * 0.5;
		}

		this._deps.setPlaneTargetY(constrain(this._deps.getPlaneTargetY() + scroll, this._deps.getMaxTargetScroll(), 0));

		this._deps.setEasePower(4);

		//Now sync the bar
		this._deps.syncBarToTargetY();
	}

	/** Pickup directional key presses to manage down, pageDown, up, pageUp */
	public onKeyPress(e: KeyboardEvent): void {
		let key: number = e.keyCode ? e.keyCode : e.which;
		let keyType: DirectionalKeyType;
		let move: number;

		if (key === 40) {
			keyType = DirectionalKeyType.DOWN;
		} else if (key === 38) {
			keyType = DirectionalKeyType.UP;
		} else if (key === 34) {
			keyType = DirectionalKeyType.PAGE_DOWN;
		} else if (key === 33) {
			keyType = DirectionalKeyType.PAGE_UP;
		} else if (key === 39) {
			keyType = DirectionalKeyType.RIGHT;
		} else if (key === 37) {
			keyType = DirectionalKeyType.LEFT;
		}

		// If this scrollbar is not actually active then dispatch the event to be used in other views (I.E in home/work to move the timeline)
		if ((!this._deps.isActive() || this._deps.isPaused() || this._deps.isHideScroller()) && keyType) {
			Events.dispatch(new DebugKeypressEvent({ keyType: keyType }));
			return;
		}

		move = this._getKeyMoveAmount(keyType);
		if (!move) {
			return;
		}

		this._deps.setPlaneTargetY(constrain(this._deps.getPlaneTargetY() + move, this._deps.getMaxTargetScroll(), 0));
		//Now sync the bar
		this._deps.syncBarToTargetY();
	}

	/** Touch drag started (registration + dragging flag stay in ScrollbarController) */
	public onTouchStart(e: TouchEvent): void {
		this._deps.setEasePower(1);
	}

	public onTouchMove(e: TouchEvent): void {
		//Set the target Y position of the scroller to move the plane:
	}

	public onTouchEnd(e: TouchEvent): void {
		this._deps.setEasePower(20);
	}

	public reduceScrollWheelRecord(): void {
		if (this._scrollWheelRecord > 0) {
			this._scrollWheelRecord -= this.SCROLLWHEEL_LIMITER_FRAME_REDUCE;
		}
	}


	/** Get a suitable key move amount  **/
	private _getKeyMoveAmount(keyType: DirectionalKeyType): number {
		//
		if (keyType === DirectionalKeyType.PAGE_UP || keyType === DirectionalKeyType.PAGE_DOWN) {
			return keyType === DirectionalKeyType.PAGE_UP ? this._deps.getInnerHeight() : -1 * this._deps.getInnerHeight();
		}
		//Assume up/down key so do mini directional:
		if (keyType === DirectionalKeyType.UP || keyType === DirectionalKeyType.DOWN) {
			return keyType === DirectionalKeyType.UP ? 100 : -100;
		}

		return 0;
	}
}
